<script setup>
import { computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useProjectStore } from '@/stores/project'
import { useOrgStore } from '@/stores/org'
import { useMessagingStore } from '@/stores/messaging'
import { useChatStore } from '@/stores/chat'
import { Plus, Search, X } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import ChatList from './ChatList.vue'
import ConversationView from './ConversationView.vue'

const projectStore = useProjectStore()

const orgStore = useOrgStore()

const messagingStore = useMessagingStore()

const chatStore = useChatStore()
const route = useRoute()
const router = useRouter()

const orgId = computed(() => orgStore.selectedOrg)
const activeKey = computed(() => messagingStore.activeKey)
const orgName = computed(() => orgStore.getSelectedOrg?.organization_name)
const newChatOpen = computed(() => messagingStore.newChatOpen)

// Keep the open conversation in the URL (deep-linkable) and the URL in the
// store — both watchers are guarded by an equality check so they can't loop.
const routeConversation = computed(() =>
  route.name === 'OrgChats' ? route.params.conversation_uuid || null : null
)

watch(
  routeConversation,
  (uuid) => {
    if (route.name !== 'OrgChats' || uuid === activeKey.value) return
    messagingStore.setActive(uuid)
    if (uuid) {
      chatStore.ensureMessages(uuid).then(() => {
        chatStore.markRead(uuid)
      })
    }
  },
  { immediate: true }
)

watch(activeKey, (uuid) => {
  if (route.name !== 'OrgChats' || uuid === routeConversation.value) return
  router.replace({
    name: 'OrgChats',
    params: { org_id: route.params.org_id, conversation_uuid: uuid || undefined },
  })
})

const query = computed({
  get: () => messagingStore.searchQuery,
  set: (value) => messagingStore.setSearchQuery(value),
})

const clearSearch = () => messagingStore.setSearchQuery('')
const openNewChat = () => messagingStore.setNewChatOpen(true)

onMounted(() => {
  messagingStore.fetchAgents()
  if (orgId.value) orgStore.fetchOrgMembers(orgId.value)
  if (!projectStore.flatedProjects?.length) {
    projectStore.fetchProjects({})
  }
})
</script>

<template>
  <Teleport v-if="!activeKey && !newChatOpen" to="#panel-actions" defer>
    <Button
      variant="secondary"
      class="w-8 h-8 p-0 rounded-full"
      type="button"
      title="New chat"
      aria-label="New chat"
      @click="openNewChat"
    >
      <Plus class="w-5 h-5" />
    </Button>
  </Teleport>

  <ConversationView v-if="activeKey" />

  <div v-else class="flex h-full min-h-0 flex-col pt-2">
    <div class="px-2 pb-2">
      <div class="relative">
        <Search class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          v-model="query"
          :placeholder="
            newChatOpen
              ? 'Search people, projects and agents'
              : orgName
                ? `Search ${orgName}`
                : 'Search'
          "
          class="h-8 w-full pl-8 pr-8 text-sm"
          @keydown.escape="clearSearch"
        />
        <button
          v-if="query"
          type="button"
          class="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          title="Clear search"
          aria-label="Clear search"
          @click="clearSearch"
        >
          <X class="h-4 w-4" />
        </button>
      </div>
    </div>

    <ChatList />
  </div>
</template>
